import axios from 'axios';
import { PLATFORM_API_URL, Platform_Refresh_Url } from './endpoints';

// Platform (identity) client — cookie-based only. The accessToken/refreshToken
// cookies are httpOnly and set by skplatform-api; nothing here ever reads or
// stores a JWT. Matches sk-catalog-frontend's axiosInstance.
export const platformApi = axios.create({
  baseURL: PLATFORM_API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// Endpoints whose own 401 is terminal — retrying them via refresh would loop.
const NO_REFRESH = ['/auth/login', '/auth/refresh', '/auth/logout'];

let refreshInFlight: Promise<void> | null = null;

function refreshSession(): Promise<void> {
  if (!refreshInFlight) {
    refreshInFlight = platformApi
      .post(Platform_Refresh_Url)
      .then(() => undefined)
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

// Single-flight silent refresh-and-retry on 401, so a burst of parallel
// requests only triggers one refresh call.
platformApi.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const refreshable =
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !NO_REFRESH.some((p) => (original.url ?? '').includes(p));

    if (refreshable) {
      try {
        await refreshSession();
        original._retried = true;
        return platformApi(original);
      } catch {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  },
);
